import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faHandshake } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

// the status bar within the donate material page for donors, shows a more visually appealing and indicative view of the list of donated materials
const StatusBar = ({ status, quantityLeft, quantityTotal, darkMode }) => {
  const { t } = useTranslation();
  const percentage =
    quantityTotal > 0 ? (quantityLeft / quantityTotal) * 100 : 0;

  const isPaused = status?.toString().toLowerCase() === "paused";

  // handle case-insensitive "new" status check
  // only consider "New" if status is "new" AND percentage is 100%
  const isNew =
    !isPaused && status?.toString().toLowerCase() === "new" && percentage === 100;

  // check if fully donated (0% remaining)
  const isFullyDonated = !isPaused && percentage === 0;

  // circle dimensions
  const circleSize = 26;
  const strokeWidth = 3;
  const radius = (circleSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progressDecimal = percentage / 100;
  const strokeDashoffset = circumference * (1 - progressDecimal);

  const getProgressColor = () => {
    if (isPaused) return "#6b7280";
    if (isNew) return "#1976D2";
    if (isFullyDonated) return "#10b981";
    if (percentage >= 80) return "#0d9668";
    if (percentage >= 60) return "#16a34a";
    if (percentage >= 40) return "#d97706";
    if (percentage >= 20) return "#ea580c";
    if (percentage > 0) return "#c0392b";
    return "#6b7280";
  };

  const getStatusText = () => {
    if (isPaused) return `${Math.round(percentage)}%`;

    // only show "New" if status is "New" AND quantity hasn't been reduced (100%)
    if (isNew && percentage === 100) return t("New");

    // otherwise show status based on percentage
    if (percentage === 0) return t("Donated");
    if (percentage < 20) return t("Almost Out");
    if (percentage < 40) return t("Low");
    return `${Math.round(percentage)}%`;
  };

  const getTextColor = () => {
    if (isPaused) return "#6b7280";
    if (isNew) return "#1976D2";
    if (isFullyDonated) return "#10b981";
    return darkMode ? "#e8e8e8" : "#1a1a1a";
  };

  const getBackgroundTrackColor = () => {
    return darkMode ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)";
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        padding: "4px 0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          padding: "4px 8px",
          borderRadius: "6px",
          transition: "all 0.2s ease",
          width: "115px",
          "&:hover": {
            backgroundColor: darkMode
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.03)",
          },
        }}
        title={isPaused ? `${t("Paused")} · ${Math.round(percentage)}% ${t("remaining")}` : `${Math.round(percentage)}% remaining (${quantityLeft}/${quantityTotal} lbs)`}
      >
        {/* Circular Progress */}
        <Box
          sx={{
            width: circleSize,
            height: circleSize,
            position: "relative",
            flexShrink: 0,
            opacity: isPaused ? 0.5 : 1,
            transition: "opacity 0.2s ease",
          }}
        >
          <svg
            width={circleSize}
            height={circleSize}
            style={{ transform: "rotate(-90deg)" }}
          >
            {/* Background track */}
            <circle
              cx={circleSize / 2}
              cy={circleSize / 2}
              r={radius}
              fill="none"
              stroke={getBackgroundTrackColor()}
              strokeWidth={strokeWidth}
            />
            {/* Progress arc */}
            <circle
              cx={circleSize / 2}
              cy={circleSize / 2}
              r={radius}
              fill="none"
              stroke={getProgressColor()}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transition: "stroke-dashoffset 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
            {/* Pause icon - two bars centered inside circle */}
            {isPaused && (
              <g transform={`rotate(90, ${circleSize / 2}, ${circleSize / 2})`}>
                <rect x={circleSize / 2 - 4} y={circleSize / 2 - 3.5} width="2.5" height="7" rx="0.5" fill="#6b7280" />
                <rect x={circleSize / 2 + 1.5} y={circleSize / 2 - 3.5} width="2.5" height="7" rx="0.5" fill="#6b7280" />
              </g>
            )}
          </svg>
        </Box>

        {/* Status Text */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "0.8125rem",
            fontWeight: isNew || isFullyDonated || isPaused ? 600 : 500,
            color: getTextColor(),
            whiteSpace: "nowrap",
            minWidth: "55px",
            lineHeight: 1,
          }}
        >
          {getStatusText()}
          {isNew && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                animation: "heartbeat 1.5s ease-in-out infinite",
                "@keyframes heartbeat": {
                  "0%, 100%": { transform: "scale(1)" },
                  "10%": { transform: "scale(1.2)" },
                  "20%": { transform: "scale(1)" },
                  "30%": { transform: "scale(1.2)" },
                  "40%": { transform: "scale(1)" },
                },
              }}
            >
              <FontAwesomeIcon
                icon={faStar}
                style={{
                  fontSize: "0.65rem",
                  color: "#1976D2",
                  filter: "drop-shadow(0 0 2px rgba(25, 118, 210, 0.5))",
                }}
              />
            </Box>
          )}
          {isFullyDonated && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { transform: "scale(1)", opacity: 1 },
                  "50%": { transform: "scale(1.15)", opacity: 0.9 },
                },
              }}
            >
              <FontAwesomeIcon
                icon={faHandshake}
                style={{
                  fontSize: "0.7rem",
                  color: "#10b981",
                  filter: "drop-shadow(0 0 2px rgba(16, 185, 129, 0.5))",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default StatusBar;